/**
 * The step editor (task 14.13) — add, edit, reorder, duplicate, remove.
 *
 * This is the surface a QA engineer uses instead of writing code, so two things
 * matter more than they would in an internal tool:
 *
 * 1. **The refusal has to point at the step.** "Invalid" on a forty-step test is
 *    useless. Every problem the server returns carries a `stepId`, and the row for
 *    that step is marked with the message attached to it.
 *
 * 2. **Validation happens as you type, but saving is decided by the server.** The
 *    same `validateTestSet` runs in both places; the client copy is a convenience,
 *    never the authority. If they ever disagree, the server wins and the editor
 *    shows what it said.
 *
 * The parameter form is GENERATED from the registry — every function's parameters,
 * types, defaults, enums and help text come from `/api/palette`. Adding a step
 * function in the automation project makes it appear here with no change to this
 * file (task 14.12d).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ParamDecl, PaletteFunction, Problem, Step, TestCase, TestSet } from '../types';
import { api } from '../api';

interface Props {
  test: TestCase;
  set: TestSet;
  palette: PaletteFunction[];
  problems: Problem[];
  onChange: (test: TestCase) => void;
}

export function StepEditor({ test, set, palette, problems, onChange }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(test.steps[0]?.id ?? null);

  const byStep = useMemo(() => {
    const map = new Map<string, Problem[]>();
    for (const p of problems) {
      if (!p.stepId) continue;
      map.set(p.stepId, [...(map.get(p.stepId) ?? []), p]);
    }
    return map;
  }, [problems]);

  const roots = useMemo(
    () => test.steps.filter((s) => s.parentId === null).sort((a, b) => a.order - b.order),
    [test.steps],
  );

  const update = useCallback(
    (steps: Step[]) => onChange({ ...test, steps: renumber(steps) }),
    [test, onChange],
  );

  const addStep = (functionName: string) => {
    const decl = palette.find((f) => f.name === functionName);
    const step: Step = {
      id: `s-${Date.now().toString(36)}`,
      functionName,
      params: Object.fromEntries(
        (decl?.params ?? [])
          .filter((p) => p.default !== undefined)
          .map((p) => [p.name, { kind: 'literal' as const, value: p.default as string }]),
      ),
      parentId: null,
      order: roots.length,
      branch: 'body',
      enabled: true,
    };
    update([...test.steps, step]);
    setSelectedId(step.id);
  };

  const move = (id: string, direction: -1 | 1) => {
    const siblings = test.steps
      .filter((s) => s.parentId === (test.steps.find((x) => x.id === id)?.parentId ?? null))
      .sort((a, b) => a.order - b.order);
    const index = siblings.findIndex((s) => s.id === id);
    const target = index + direction;
    if (target < 0 || target >= siblings.length) return;

    const reordered = [...siblings];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    const orderById = new Map(reordered.map((s, i) => [s.id, i]));
    update(test.steps.map((s) => (orderById.has(s.id) ? { ...s, order: orderById.get(s.id)! } : s)));
  };

  const duplicate = (id: string) => {
    const original = test.steps.find((s) => s.id === id);
    if (!original) return;
    // A duplicate must NOT copy `captureAs`: two steps writing the same variable is
    // almost never intended and produces a confusing later failure.
    const copy: Step = {
      ...original,
      id: `s-${Date.now().toString(36)}`,
      order: original.order + 1,
      captureAs: undefined,
    };
    update([...test.steps, copy]);
    setSelectedId(copy.id);
  };

  const remove = (id: string) => {
    // Removing a control-flow step removes what it contains; leaving orphans would
    // save as an invalid set the operator cannot see or reach.
    const doomed = new Set<string>([id]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const s of test.steps) {
        if (s.parentId && doomed.has(s.parentId) && !doomed.has(s.id)) {
          doomed.add(s.id);
          grew = true;
        }
      }
    }
    update(test.steps.filter((s) => !doomed.has(s.id)));
    if (doomed.has(selectedId ?? '')) setSelectedId(null);
  };

  const patch = (id: string, changes: Partial<Step>) =>
    update(test.steps.map((s) => (s.id === id ? { ...s, ...changes } : s)));

  const selected = test.steps.find((s) => s.id === selectedId) ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
      <div className="surface p-0" data-testid="step-list">
        <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: "1px solid hsl(var(--border))" }}>
          <span className="text-sm font-medium">{test.name || 'Untitled test'}</span>
          <AddStep palette={palette} onAdd={addStep} />
        </div>

        <ol>
          {roots.length === 0 && (
            <li className="p-6 text-center text-sm opacity-70">
              No steps yet. Add one to begin — or record a flow and edit the draft.
            </li>
          )}
          {roots.map((step, index) => (
            <StepRow
              key={step.id}
              step={step}
              index={index}
              all={test.steps}
              palette={palette}
              problems={byStep}
              selectedId={selectedId}
              depth={0}
              onSelect={setSelectedId}
              onMove={move}
              onDuplicate={duplicate}
              onRemove={remove}
              onPatch={patch}
            />
          ))}
        </ol>
      </div>

      <div className="surface p-4" data-testid="step-inspector">
        {selected ? (
          <ParamForm
            step={selected}
            decl={palette.find((f) => f.name === selected.functionName)}
            set={set}
            test={test}
            problems={byStep.get(selected.id) ?? []}
            onPatch={(changes) => patch(selected.id, changes)}
          />
        ) : (
          <p className="text-sm opacity-70">Select a step to edit it.</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function StepRow(props: {
  step: Step;
  index: number;
  all: Step[];
  palette: PaletteFunction[];
  problems: Map<string, Problem[]>;
  selectedId: string | null;
  depth: number;
  onSelect: (id: string) => void;
  onMove: (id: string, d: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  onPatch: (id: string, changes: Partial<Step>) => void;
}) {
  const { step, index, all, palette, problems, selectedId, depth } = props;
  const decl = palette.find((f) => f.name === step.functionName);
  const stepProblems = problems.get(step.id) ?? [];
  const errors = stepProblems.filter((p) => p.severity === 'error');
  const children = all.filter((s) => s.parentId === step.id);

  return (
    <>
      <li
        data-testid={`step-${step.id}`}
        data-invalid={errors.length > 0 ? 'true' : 'false'}
        className="flex items-start gap-2 py-2 pr-4 text-sm transition"
        style={{
          paddingLeft: `${1 + depth * 1.5}rem`,
          // Token-based, not a Tailwind palette class. The earlier version used
          //  for the selected row, which is near-white and made the
          // text and icons on it invisible against this dark theme.
          background: selectedId === step.id ? 'hsl(var(--bg-overlay))' : undefined,
          borderLeft: errors.length
            ? '3px solid hsl(var(--fail))'
            : selectedId === step.id
              ? '3px solid hsl(var(--accent))'
              : '3px solid transparent',
          opacity: step.enabled === false ? 0.5 : 1,
        }}
      >
        <button
          className="flex-1 text-left"
          onClick={() => props.onSelect(step.id)}
          data-testid={`select-step-${step.id}`}
        >
          <span className="mr-2 tabular-nums opacity-50">{index + 1}</span>
          {step.placeholder ? (
            <>
              <span className="pill pill-info">pending</span>{' '}
              <span className="italic">{step.placeholder.description}</span>
            </>
          ) : (
            <>
              <span className="font-medium">{step.label || decl?.summary || step.functionName}</span>
              {step.captureAs && <span className="ml-2 opacity-60">→ {`{{${step.captureAs}}}`}</span>}
            </>
          )}
          {errors.length > 0 && (
            <p className="mt-1 text-xs" style={{ color: 'hsl(var(--fail))' }} data-testid={`step-error-${step.id}`}>
              {errors[0].message}
            </p>
          )}
        </button>

        <div className="flex shrink-0 gap-1">
          <IconButton label="Move up" testid={`up-${step.id}`} onClick={() => props.onMove(step.id, -1)}>↑</IconButton>
          <IconButton label="Move down" testid={`down-${step.id}`} onClick={() => props.onMove(step.id, 1)}>↓</IconButton>
          <IconButton label="Duplicate" testid={`dup-${step.id}`} onClick={() => props.onDuplicate(step.id)}>⧉</IconButton>
          <IconButton label="Remove" testid={`del-${step.id}`} onClick={() => props.onRemove(step.id)}>✕</IconButton>
        </div>
      </li>

      {(['then', 'else', 'body'] as const).map((branch) => {
        const inBranch = children.filter((c) => c.branch === branch).sort((a, b) => a.order - b.order);
        if (!inBranch.length) return null;
        return (
          <li key={branch} style={{ background: 'hsl(var(--bg) / 0.4)' }}>
            {decl?.branches && (
              <div className="px-4 py-1 text-xs uppercase opacity-60" style={{ paddingLeft: `${2 + depth * 1.5}rem` }}>
                {branch}
              </div>
            )}
            <ol>
              {inBranch.map((child, i) => (
                <StepRow key={child.id} {...props} step={child} index={i} depth={depth + 1} />
              ))}
            </ol>
          </li>
        );
      })}
    </>
  );
}

function IconButton({ label, testid, onClick, children }: { label: string; testid: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      aria-label={label}
      title={label}
      data-testid={testid}
      onClick={onClick}
      className="rounded px-2 py-1 text-xs transition"
      style={{ color: 'hsl(var(--text-muted))' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--bg-overlay))'; e.currentTarget.style.color = 'hsl(var(--text))'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'hsl(var(--text-muted))'; }}
    >
      {children}
    </button>
  );
}

function AddStep({ palette, onAdd }: { palette: PaletteFunction[]; onAdd: (name: string) => void }) {
  const grouped = useMemo(() => {
    const map = new Map<string, PaletteFunction[]>();
    for (const f of palette) map.set(f.category, [...(map.get(f.category) ?? []), f]);
    return [...map.entries()];
  }, [palette]);

  return (
    <select
      data-testid="add-step"
      value=""
      onChange={(e) => e.target.value && onAdd(e.target.value)}
      className="field"
    >
      <option value="">+ Add step…</option>
      {grouped.map(([category, fns]) => (
        <optgroup key={category} label={category}>
          {fns.map((f) => (
            <option key={f.name} value={f.name} title={f.description}>
              {f.summary}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/**
 * The parameter form, generated entirely from the registry declaration.
 *
 * Advanced parameters are collapsed by default (task 14.3): the full surface stays
 * reachable without making the common case a wall of fields.
 */
function ParamForm({
  step, decl, set, test, problems, onPatch,
}: {
  step: Step;
  decl?: PaletteFunction;
  set: TestSet;
  test: TestCase;
  problems: Problem[];
  onPatch: (changes: Partial<Step>) => void;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  if (step.placeholder) {
    return (
      <div className="flex flex-col gap-3">
        <span className="pill pill-info">pending</span>
        <label className="text-sm font-medium">What should this step do?</label>
        <textarea
          data-testid="placeholder-description"
          className="field min-h-24"
          value={step.placeholder.description}
          onChange={(e) => onPatch({ placeholder: { ...step.placeholder!, description: e.target.value } })}
        />
        <p className="text-xs opacity-70">
          The test still saves and runs. This step reports as pending rather than failing,
          and appears in the developer queue.
        </p>
      </div>
    );
  }

  if (!decl) {
    return <p className="text-sm" style={{ color: 'hsl(var(--fail))' }}>No registered function named “{step.functionName}”.</p>;
  }

  const common = decl.params.filter((p) => !p.advanced);
  const advanced = decl.params.filter((p) => p.advanced);

  const setParam = (name: string, raw: string) => {
    // The tagged union is inferred from what was typed, so an operator never has to
    // write {"kind":"literal"} by hand.
    const value = raw.startsWith('=')
      ? { kind: 'expression' as const, source: raw.slice(1) }
      : /^\{\{[^}]+\}\}$/.test(raw)
        ? { kind: 'variable' as const, name: raw.slice(2, -2).trim() }
        : /^\[[^\]]+\]$/.test(raw)
          ? { kind: 'column' as const, column: raw.slice(1, -1).trim() }
          : { kind: 'literal' as const, value: raw };
    onPatch({ params: { ...step.params, [name]: value } });
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold">{decl.summary}</h3>
        <p className="mt-1 text-xs" style={{ color: "hsl(var(--text-faint))" }}>{decl.description}</p>
      </div>

      {problems.length > 0 && (
        <ul
          className="rounded-md p-2 text-xs"
          style={{ background: 'hsl(var(--fail) / 0.1)', color: 'hsl(var(--fail))' }}
          data-testid="inspector-problems"
        >
          {problems.map((p, i) => <li key={i}>{p.message}</li>)}
        </ul>
      )}

      {common.map((p) => (
        <ParamField key={p.name} decl={p} step={step} set={set} onChange={(v) => setParam(p.name, v)} />
      ))}

      {advanced.length > 0 && (
        <>
          <button
            className="self-start text-xs underline" style={{ color: "hsl(var(--accent))" }}
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="toggle-advanced"
          >
            {showAdvanced ? 'Hide' : 'Show'} {advanced.length} advanced option{advanced.length > 1 ? 's' : ''}
          </button>
          {showAdvanced &&
            advanced.map((p) => (
              <ParamField key={p.name} decl={p} step={step} set={set} onChange={(v) => setParam(p.name, v)} />
            ))}
        </>
      )}

      {decl.returns && (
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Capture result as</span>
          <input
            data-testid="capture-as"
            className="field"
            placeholder="e.g. courseId"
            value={step.captureAs ?? ''}
            onChange={(e) => onPatch({ captureAs: e.target.value || undefined })}
          />
          <span className="text-xs opacity-70">
            {decl.returns.description} Reference it later as {'{{name}}'}.
          </span>
        </label>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="step-enabled"
          checked={step.enabled !== false}
          onChange={(e) => onPatch({ enabled: e.target.checked })}
        />
        Enabled
      </label>
    </div>
  );
}

function ParamField({
  decl, step, set, onChange,
}: {
  decl: ParamDecl;
  step: Step;
  set: TestSet;
  onChange: (value: string) => void;
}) {
  const current = step.params[decl.name];
  const text =
    current?.kind === 'literal' ? String(current.value ?? '')
    : current?.kind === 'variable' ? `{{${current.name}}}`
    : current?.kind === 'expression' ? `=${current.source}`
    : current?.kind === 'column' ? `[${current.column}]`
    : '';

  const options = decl.options?.map((o) => o.value)
    ?? (decl.optionsFrom === 'tables' ? set.dataTables.map((t) => t.name)
      : decl.optionsFrom === 'fragments' ? set.fragments.map((f) => f.name)
      : undefined);

  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">
        {decl.name}
        {decl.required && <span className="ml-1" style={{ color: 'hsl(var(--fail))' }}>*</span>}
      </span>

      {options ? (
        <select
          data-testid={`param-${decl.name}`}
          className="field"
          value={text}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{decl.default !== undefined ? `${decl.default} (default)` : '—'}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          data-testid={`param-${decl.name}`}
          className="field"
          placeholder={decl.default !== undefined ? `${decl.default} (default)` : decl.example ?? ''}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      <span className="text-xs" style={{ color: "hsl(var(--text-faint))" }}>{decl.description}</span>
    </label>
  );
}

/** Keeps sibling order dense (0,1,2…) after a move or delete. */
function renumber(steps: Step[]): Step[] {
  const groups = new Map<string, Step[]>();
  for (const s of steps) {
    const key = `${s.parentId ?? 'root'}:${s.branch}`;
    groups.set(key, [...(groups.get(key) ?? []), s]);
  }
  const out: Step[] = [];
  for (const group of groups.values()) {
    group.sort((a, b) => a.order - b.order).forEach((s, i) => out.push({ ...s, order: i }));
  }
  return out;
}
